import { useState } from "react";
import { motion } from "motion/react";
import { ArrowRight } from "lucide-react";
import { AGENTS, AGENT_ORDER, DATA_FLOWS, type AgentId } from "@/data/transparency";
import AgentCard from "./AgentCard";
import AgentDetailModal from "./AgentDetailModal";

export default function AgentFlowDiagram() {
  const [selectedAgent, setSelectedAgent] = useState<AgentId | null>(null);

  return (
    <div className="w-full">
      {/* Flow Diagram */}
      <div className="relative">
        {/* Desktop Layout - Horizontal */}
        <div className="hidden lg:flex items-stretch justify-center gap-4">
          {AGENT_ORDER.map((agentId, index) => {
            const agent = AGENTS[agentId];
            const flow = DATA_FLOWS.find(f => f.from === agentId);
            
            return (
              <div key={agentId} className="flex items-center">
                <div className="w-80">
                  <AgentCard
                    agent={agent}
                    onClick={() => setSelectedAgent(agentId)}
                    isActive={selectedAgent === agentId}
                    delay={index * 0.15}
                  />
                </div>
                
                {/* Connection Arrow */}
                {index < AGENT_ORDER.length - 1 && flow && (
                  <motion.div
                    className="flex flex-col items-center mx-4 min-w-[100px]"
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.6 + index * 0.2, duration: 0.5 }}
                  >
                    <div className="relative flex items-center">
                      {/* Animated Arrow */}
                      <div className="w-16 h-0.5 bg-gradient-to-r from-gray-200 to-[#D4893E]/50 relative overflow-hidden">
                        <motion.div
                          className="absolute inset-0 bg-gradient-to-r from-transparent via-[#D4893E] to-transparent"
                          animate={{ x: ["-100%", "200%"] }}
                          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                        />
                      </div>
                      <ArrowRight className="w-5 h-5 text-[#D4893E] -ml-1" />
                    </div>
                    <span className="text-xs text-slate-500 mt-2 text-center whitespace-nowrap">
                      {flow.label}
                    </span>
                  </motion.div>
                )}
              </div>
            );
          })}
        </div>

        {/* Tablet/Mobile Layout - Vertical */}
        <div className="lg:hidden space-y-4">
          {AGENT_ORDER.map((agentId, index) => {
            const agent = AGENTS[agentId];
            const flow = DATA_FLOWS.find(f => f.from === agentId);
            
            return (
              <div key={agentId}>
                <AgentCard
                  agent={agent}
                  onClick={() => setSelectedAgent(agentId)}
                  isActive={selectedAgent === agentId}
                  delay={index * 0.15}
                />
                
                {/* Connection Arrow - Vertical */}
                {index < AGENT_ORDER.length - 1 && flow && (
                  <motion.div
                    className="flex flex-col items-center py-4"
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.3 + index * 0.2, duration: 0.5 }}
                  >
                    <div className="h-8 w-0.5 bg-gradient-to-b from-gray-200 to-[#D4893E]/50 relative overflow-hidden">
                      <motion.div
                        className="absolute inset-0 bg-gradient-to-b from-transparent via-[#D4893E] to-transparent"
                        animate={{ y: ["-100%", "200%"] }}
                        transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                      />
                    </div>
                    <div className="flex items-center gap-2 mt-2">
                      <ArrowRight className="w-4 h-4 text-[#D4893E] rotate-90" />
                      <span className="text-xs text-slate-500">
                        {flow.label}
                      </span>
                    </div>
                  </motion.div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Detail Modal */}
      {selectedAgent && (
        <AgentDetailModal
          agent={AGENTS[selectedAgent]}
          onClose={() => setSelectedAgent(null)}
        />
      )}
    </div>
  );
}
